import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AUTH_ENABLED } from './shared/infra/auth/AuthConfig';
import { AuthContext } from './shared/infra/auth/AuthContext';
import { supabase } from './shared/infra/auth/SupabaseClient';
import Login from './pages/login/Login';
import ResetPassword from './pages/login/ResetPassword';
import DocMarkup from './pages/documentation/MarkupDocumentation';
import DraftEditor from './pages/draft/DraftEditor';
import DraftList from './pages/draft/DraftList';
import StartPageDraft from './pages/draft/StartPageDraft';
import PageViewer from './pages/lecture/Lecture';
import NavigationMenu from './pages/navigation/NavigationMenu';
import Editor from './pages/editor/Editor';
import Home from './pages/home/Home';
import Contact from './pages/contact/Contact';
import Course from './pages/course/Course';
import Courses from './pages/courses/Courses';
import StartCourseDraft from './pages/course-editor/StartCourseDraft';
import CourseDraftEditor from './pages/course-editor/CourseDraftEditor';
import CourseDraftList from './pages/course-editor/CourseDraftList';

function RequireEditor({ canEdit, ready, children }) {
  const location = useLocation();
  if (!ready) return <p><output>Checking session…</output></p>;
  return canEdit ? children : <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
}

function LoginRoute({ canEdit }) {
  const location = useLocation();
  const next = new URLSearchParams(location.search).get('next');
  const destination = next?.startsWith('/') && !next.startsWith('//') && !next.startsWith('/login') ? next : '/';
  return canEdit ? <Navigate to={destination} replace /> : <Login />;
}

export default function App() {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(!AUTH_ENABLED);
  useEffect(() => {
    if (!AUTH_ENABLED) return;
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) { setSession(data.session); setReady(true); }
    }).catch(() => { if (active) setReady(true); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active) { setSession(session); setReady(true); }
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, []);
  const canEdit = !AUTH_ENABLED || Boolean(session);
  const protect = element => <RequireEditor canEdit={canEdit} ready={ready}>{element}</RequireEditor>;
  return <AuthContext.Provider value={{ canEdit }}><BrowserRouter>
    <NavigationMenu isAuthenticated={AUTH_ENABLED && Boolean(session)} />
    <Routes>
      <Route path="/" element={<Courses />} />
      <Route path="/home" element={<Home />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/courses" element={<Courses />} />
      <Route path="/course" element={<Navigate to="/courses" replace />} />
      <Route path="/course/:courseId" element={<Course />} />
      <Route path="/lecture" element={<PageViewer />} />
      <Route path="/editor" element={protect(<Editor />)} />
      <Route path="/editor/course/new" element={protect(<StartCourseDraft />)} />
      <Route path="/editor/course/:courseId/edit" element={protect(<StartCourseDraft />)} />
      <Route path="/editor/course-drafts" element={protect(<CourseDraftList />)} />
      <Route path="/editor/course-drafts/:draftId" element={protect(<CourseDraftEditor />)} />
      <Route path="/editor/page/:pageId/edit" element={protect(<StartPageDraft />)} />
      <Route path="/draft" element={protect(<DraftEditor mode="edit" />)} />
      <Route path="/drafts" element={protect(<DraftList />)} />
      <Route path="/editor/playground" element={<DraftEditor mode="playground" />} />
      <Route path="/editor/doc/editor/markup" element={<DocMarkup />} />
      <Route path="/doc/editor/markup" element={<DocMarkup />} />
      <Route path="/login" element={<LoginRoute canEdit={ready && canEdit} />} />
      <Route path="/reset-password" element={AUTH_ENABLED ? <ResetPassword /> : <Navigate to="/" replace />} />
    </Routes>
  </BrowserRouter></AuthContext.Provider>;
}
